import React, { useRef, useState } from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormClearErrors,
} from "react-hook-form";
import {
  MAX_TAGLINE_LENGTH_MAX,
  TAGS,
  MAX_IMAGE_SIZE_MB,
  MAX_IMAGE_SIZE_BYTES,
  serviceTypeVendor,
} from "../../lib/constants";
import TagSelector from "../../components/TagSelector";
import { FiTrash2 } from "react-icons/fi";
import {
  fileToBase64,
  IsServiceTypeVendor,
  useDecodedTokenResult,
  validateFileType,
} from "../../lib/utils";
import InfoTooltip from "../../components/common/InfoTooltip";
import { useLocation } from "react-router-dom";
import { useToast } from "../../@/components/ui/use-toast";
import StorePreviewPage from "./StorePreviewPage";

type StoreFormInputs = {
  vendorImage: string;
  vendorImageType: string;
  vendorImageBlob: string;
  wallpaperImageBlob: string;
  wallpaperImageType: string;
  wallpaperImageUrl: string;
  themeColor: string;
  company: string;
  storeTags?: Tag[];
  removedTags?: RemovedTags[];
  website: string;
  aboutStore: string;
  returnInfo: string;
  tagLine?: string;
  serviceTypeId: number;
};

interface Tag {
  id: number;
  name: string;
  tagId?: number;
  vendorId?: number;
}

interface RemovedTags {
  id: number;
}

type StoreInformationProps = {
  register: UseFormRegister<StoreFormInputs>;
  errors: FieldErrors<StoreFormInputs>;
  clearErrors: UseFormClearErrors<StoreFormInputs>;
  watch: any;
  setValue: any;
  vendorTags: any;
  setRemovedTags: any;
  removedTags: RemovedTags[];
  selectedTags: Tag[];
  setSelectedTags: any;
  setShowAlertOnRouteChange: React.Dispatch<React.SetStateAction<boolean>>;
};

export const StoreInformation: React.FC<StoreInformationProps> = ({
  register,
  errors,
  clearErrors,
  watch,
  setValue,
  vendorTags,
  removedTags,
  setRemovedTags,
  selectedTags,
  setSelectedTags,
  setShowAlertOnRouteChange,
}) => {
  const themeColor = watch("themeColor");
  const [tagline, setTagline] = useState("");
  const location = useLocation();
  const { toast } = useToast();
  const { serviceTypeId } = useDecodedTokenResult();
  const storeLogo =
    watch("vendorImageBlob") || watch("vendorImage") || "storeDummyImg.png";
  const vendorName = watch("company");
  const handleTaglineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowAlertOnRouteChange(true);
    const value = e.target.value;
    if (value.length <= MAX_TAGLINE_LENGTH_MAX) {
      setTagline(value);
      setValue("tagLine", value);
      clearErrors("tagLine");
    } else {
      clearErrors("tagLine");
      setTagline(value.slice(0, MAX_TAGLINE_LENGTH_MAX));
      setValue("tagLine", value.slice(0, MAX_TAGLINE_LENGTH_MAX));
    }
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowAlertOnRouteChange(true);
    const newColor = e.target.value;
    setValue("themeColor", newColor);
  };

  const getQueryParams = (param: string) => {
    const params = new URLSearchParams(location.search);
    return params.get(param);
  };

  // Get the selectedTab value from query parameters
  const paramGetServiceTypeId = getQueryParams("serviceTypeId");
  const [dragging, setDragging] = useState(false);
  const [draggingWallpaper, setDraggingWallpaper] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const wallpaperInputRef = useRef<HTMLInputElement | null>(null);

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "wallpaper" | "logo"
  ) => {
    setShowAlertOnRouteChange(true);
    const file = e.target.files?.[0];

    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast({
        title: "Error!",
        description: `File size exceeds the maximum limit of ${MAX_IMAGE_SIZE_MB}MB.`,
      });
      // Reset the input
      if (type === "wallpaper") {
        if (wallpaperInputRef.current) wallpaperInputRef.current.value = "";
      } else {
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
      return;
    }

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    if (!validateFileType(file, allowedTypes)) {
      toast({
        title: "Error!",
        description:
          "Invalid file type. Please upload a valid image file (jpg, jpeg, png, gif).",
      });
      return;
    }

    const base64 = await fileToBase64(file);
    if (type === "wallpaper") {
      setValue("wallpaperImageBlob", base64);
      setValue("wallpaperImageType", file.type);
    } else {
      setValue("vendorImageBlob", base64);
      setValue("vendorImageType", file.type);
    }
  };

  const handleDrop = async (
    e: React.DragEvent<HTMLDivElement>,
    type: "wallpaper" | "logo"
  ) => {
    setShowAlertOnRouteChange(true);
    e.preventDefault();
    setDragging(false);
    setDraggingWallpaper(false);

    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast({
        title: "Error!",
        description: `File size exceeds the maximum limit of ${MAX_IMAGE_SIZE_MB}MB.`,
      });
      return;
    }

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    if (!validateFileType(file, allowedTypes)) {
      toast({
        title: "Error!",
        description:
          "Invalid file type. Please upload a valid image file (jpg, jpeg, png, gif).",
      });

      return;
    }

    const base64 = await fileToBase64(file);
    if (type === "wallpaper") {
      setValue("wallpaperImageBlob", base64);
      setValue("wallpaperImageType", file.type);
    } else {
      setValue("vendorImageBlob", base64);
      setValue("vendorImageType", file.type);
    }
  };

  const handleDeleteImage = (type: "wallpaper" | "logo") => {
    if (type === "wallpaper") {
      setValue("wallpaperImageBlob", "");
      setValue("wallpaperImageType", "");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else {
      setValue("vendorImageBlob", "");
      setValue("vendorImageType", "");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-2">
      {/* Left Column */}
      <div className="space-y-6">
        {/* Store Information Section */}
        <div className="flex flex-col p-4 bg-white shadow-sm rounded-lg items-start space-y-5 justify-center">
          <h1 className="text-xl md:text-2xl">Store Information</h1>
          <div className="w-full">
            <label className="block text-sm font-helveticaMedium text-customGrayDark">
              Store Name<span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              {...register("company", { required: true })}
              className="flex-1 mt-1 w-full text-sm bg-grayBackgroundColor px-3 py-2 border border-gray-300 focus:outline-customGreenColor rounded-md shadow-sm"
              placeholder="Walgreens"
            />
            {errors.company && (
              <p className="text-red-500 text-xs mt-1">
                Store name is required.
              </p>
            )}
          </div>

          <div className="w-full">
            <label className="flex items-center text-sm font-helveticaMedium text-customGrayDark">
              <span>Tagline</span>
              <span className="font-helveticaLight text-customGrayText text-xs ml-1">
                (Optional)
              </span>
              <InfoTooltip content="A short, catchy phrase that captures your brand's essence and makes it memorable to customers." />
            </label>
            <input
              className="flex-1 mt-1 w-full text-sm bg-grayBackgroundColor px-3 py-2 border border-gray-300 rounded-md shadow-sm"
              value={watch("tagLine") || tagline}
              onChange={handleTaglineChange}
              placeholder="Tagline"
            />
            <p className="text-sm text-gray-500 mt-1">{`${
              watch("tagLine")?.length || tagline.length
            }/${MAX_TAGLINE_LENGTH_MAX} characters`}</p>
            {errors.tagLine && (
              <p className="text-red-500 text-xs mt-1">
                {errors.tagLine.message}
              </p>
            )}
          </div>

          <div className="w-full">
            <label className="flex items-center  text-sm font-helveticaMedium text-customGrayDark">
              <span>Store Tags</span>
              <span className="font-helveticaLight text-customGrayText text-xs ml-1">
                (Optional)
              </span>
              <InfoTooltip content="Select tags that best describe your store's offerings (e.g., Organic, Vegan, Local Products). These tags help customers discover your store when searching for specific products or categories." />
            </label>

            <TagSelector
              fetchTagId={TAGS.VendorTags}
              setSelectedTags={setSelectedTags}
              selectedTags={selectedTags}
              preloadedTags={vendorTags}
              removedTags={removedTags}
              setRemovedTags={setRemovedTags}
              setShowAlertOnRouteChange={setShowAlertOnRouteChange}
              serviceTypeId={serviceTypeId}
              paramGetServiceTypeId={Number(paramGetServiceTypeId)}
            />
          </div>

          <div className="w-full">
            <label className="flex items-center text-sm font-helveticaMedium text-customGrayDark">
              <span>Store Website</span>
              <InfoTooltip content="Enter your store's official website URL. This helps build trust with customers and allows them to learn more about your business, view your full product catalog, or access additional services you offer." />
            </label>
            <input
              type="text"
              {...register("website")}
              className="focus:outline-customGreenColor flex-1 mt-1 w-full text-sm bg-grayBackgroundColor px-3 py-2 border border-gray-300 rounded-md shadow-sm"
              placeholder="walgreens.com"
            />
            {errors.website && (
              <p className="text-red-500 text-xs mt-1">
                {errors.website.message}
              </p>
            )}
          </div>
        </div>

        {/* About Store Section */}
        <div className="flex flex-col p-4 bg-white shadow-sm rounded-lg items-start space-y-1 justify-center w-full">
          <label className="flex items-center text-sm font-helveticaMedium text-customGrayDark">
            <h1 className="mr-1">About Store</h1>
            <span className="font-helveticaLight  text-customGrayText">
              {" "}
              (Optional)
            </span>
            <InfoTooltip content="Share your store's story, mission, and values. Include details about your business history, specialties, and what sets you apart from others. A compelling description can help build trust and connect with potential customers." />
          </label>
          <div className="w-full">
            <textarea
              {...register("aboutStore")}
              className="focus:outline-customGreenColor flex-1 mt-1 w-full text-sm bg-grayBackgroundColor px-3 py-2 resize-none h-[20vh] md:h-[25vh] border border-gray-300 rounded-md shadow-sm"
              placeholder="About the store..."
            />
          </div>
        </div>
        <div className="flex flex-col p-4 bg-white shadow-sm rounded-lg items-start space-y-1 justify-center">
          <label className="flex items-center text-sm font-helveticaMedium text-customGrayDark">
            <h1>Refund Policy</h1>
            <InfoTooltip content="Clearly outline your store's return and refund policies. Include important details such as return windows, condition requirements, refund methods, and any specific procedures customers need to follow. Clear policies help set customer expectations and reduce potential disputes." />
          </label>
          <div className="w-full">
            <textarea
              {...register("returnInfo")}
              className="focus:outline-customGreenColor flex-1 mt-1 w-full text-sm bg-grayBackgroundColor px-3 py-2 resize-none h-[20vh] md:h-[25vh] border border-gray-300 rounded-md shadow-sm"
              placeholder="Refund Info..."
            />
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div className="space-y-6">
        {/* Store Logo Section */}
        <div className="bg-white p-4 shadow-sm rounded-lg">
          <div>
            <label className="flex items-center text-sm font-helveticaMedium text-customGrayDark">
              <span>Store Logo</span>
              <InfoTooltip content="Upload your store's logo image. This will be displayed prominently on your store page and in search results. Use a high-quality image that clearly represents your brand." />
            </label>

            <div className="flex flex-col md:flex-row justify-between gap-3">
              <div className="flex items-center space-x-1 relative">
                <img
                  src={
                    watch("vendorImageBlob") ||
                    watch("vendorImage") ||
                    "storeDummyImg.png"
                  }
                  alt="Store Logo"
                  className="h-20 w-28 object-contain border border-gray-300 rounded-lg"
                />
                {watch("vendorImageBlob") && (
                  <button
                    type="button"
                    onClick={() => handleDeleteImage("logo")}
                    className="text-red-500 hover:text-red-700 absolute right-0 bottom-7 bg-white rounded-md border border-gray-300"
                  >
                    <FiTrash2 size={20} />
                  </button>
                )}
              </div>
              <div
                className={`mt-2 border-2 border-dashed rounded-md h-40 w-full flex flex-col justify-center items-center p-6 
                        ${
                          dragging
                            ? "border-customGreenColor"
                            : "border-gray-300"
                        }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => handleDrop(e, "logo")}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  accept=".jpg,.jpeg,.gif,.png"
                  {...register("vendorImage")}
                  onChange={(e) => handleImageUpload(e, "logo")}
                  ref={fileInputRef}
                  className="hidden"
                />
                <p className="text-sm text-neutralDGrey">Drag Image Here</p>
                <p className="text-sm text-neutralLGrey">OR</p>
                <p className="text-neutralDGrey cursor-pointer text-sm">
                  Click To Upload
                </p>
                <p className="text-xs text-neutralLGrey mt-2">
                  Allowed file extensions: jpg, jpeg, gif, png
                </p>
                <p className="text-xs text-neutralLGrey">
                  Maximum file size: {MAX_IMAGE_SIZE_MB}MB
                </p>
                <p className="text-xs text-neutralLGrey">
                  Recommended size: 80x80px
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Cover Image Section */}
        {serviceTypeVendor.Grocery !== serviceTypeId &&
        Number(paramGetServiceTypeId) !== serviceTypeVendor.Grocery ? (
          <div className="bg-white p-4 shadow-sm rounded-lg">
            <div>
              <label className="flex items-center text-sm font-helveticaMedium text-customGrayDark">
                <span>Cover Image</span>
                <InfoTooltip content="Upload a cover image that showcases your store's atmosphere or products. This image will appear at the top of your store page." />
              </label>

              <div className="flex flex-col gap-3">
                <div
                  className={`mt-2 border-2 border-dashed rounded-md h-40 w-full flex flex-col justify-center items-center p-6 
                        ${
                          draggingWallpaper
                            ? "border-customGreenColor"
                            : "border-gray-300"
                        }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDraggingWallpaper(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => handleDrop(e, "wallpaper")}
                  onClick={() => wallpaperInputRef.current?.click()}
                >
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.gif,.png"
                    {...register("wallpaperImageBlob")}
                    onChange={(e) => handleImageUpload(e, "wallpaper")}
                    ref={wallpaperInputRef}
                    className="hidden"
                  />
                  <p className="text-sm text-neutralDGrey">Drag Image Here</p>
                  <p className="text-sm text-neutralLGrey">OR</p>
                  <p className="text-neutralDGrey cursor-pointer text-sm">
                    Click To Upload
                  </p>
                  <p className="text-xs text-neutralLGrey mt-2">
                    Allowed file extensions: jpg, jpeg, png
                  </p>
                  <p className="text-xs text-neutralLGrey">
                    Maximum file size: {MAX_IMAGE_SIZE_MB}MB
                  </p>
                  <p className="text-xs text-neutralLGrey">
                    Recommended size: 364x168px
                  </p>
                </div>
                <div className="flex items-center space-x-1 relative">
                  <img
                    src={
                      watch("wallpaperImageBlob") ||
                      watch("wallpaperImageUrl") ||
                      "storeDummyImg.png"
                    }
                    alt="coverImage"
                    className="h-48 md:h-80 w-full object-contain border border-gray-300 rounded-lg"
                  />
                  {watch("wallpaperImageBlob") && (
                    <button
                      type="button"
                      onClick={() => handleDeleteImage("wallpaper")}
                      className="text-red-500 hover:text-red-700 absolute right-3 top-4 bg-white rounded-md border border-gray-300"
                    >
                      <FiTrash2 size={20} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Theme Color Section */}
        <div className="bg-white p-4 shadow-sm rounded-lg">
          <div className="mt-1 mb-4">
            <label className="flex items-center text-sm font-helveticaMedium text-customGrayDark">
              <span>Theme Color</span>
              <InfoTooltip content="Select a color that matches your brand identity. This color will be featured throughout your store page, including headers and interactive elements. Choose a color that enhances visibility while maintaining consistency with your brand guidelines." />
            </label>
            <div
              className="flex items-center bg-grayBackgroundColor px-3 py-2 justify-between border rounded-md border-gray-300 cursor-pointer"
              onClick={() => {
                const colorInput = document.querySelector(
                  'input[type="color"]'
                ) as HTMLInputElement;
                colorInput?.click();
              }}
            >
              <span className="mr-4 text-sm font-medium text-customGrayDark">
                {themeColor}
              </span>
              <input
                type="color"
                {...register("themeColor")}
                onChange={handleColorChange}
                className="w-6 h-6 border-none bg-transparent p-0 cursor-pointer"
              />
            </div>
          </div>
          <StorePreviewPage
            themeColor={themeColor}
            logoUrl={storeLogo}
            vendorName={vendorName}
          />
        </div>
      </div>
    </div>
  );
};
